import { Link } from '@tanstack/react-router';

import BudgyWordmark, { BudgyMark } from '@/components/BudgyWordmark';
import { Button } from '@/components/ui/button';
import { setStatusCode404 } from '@/utils/setStatusCode404';

const NotFoundPage = () => {
  setStatusCode404();

  return (
    <div className="relative min-h-dvh overflow-hidden bg-canvas text-ink">
      <title>Page not found — Budgy</title>
      <meta name="robots" content="noindex, follow" />

      <div
        aria-hidden="true"
        className="absolute -top-32 -right-32 size-80 rounded-full bg-accent-tint sm:size-[28rem]"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-44 -left-32 size-80 rounded-full bg-chip sm:size-[30rem]"
      />

      <div className="relative mx-auto flex min-h-dvh max-w-[1160px] flex-col px-[clamp(20px,4vw,48px)]">
        <header>
          <nav aria-label="Primary" className="flex h-20 items-center">
            <Link
              aria-label="Budgy home"
              className="flex items-center gap-2 text-ink no-underline"
              to="/"
            >
              <BudgyWordmark />
            </Link>
          </nav>
        </header>

        <main className="grid flex-1 items-center gap-12 py-12 lg:grid-cols-[minmax(0,1fr)_minmax(380px,0.8fr)] lg:gap-20">
          <section className="flex max-w-[650px] flex-col items-start">
            <p className="m-0 text-sm font-semibold tracking-[0.12em] text-accent-strong uppercase">
              Error 404
            </p>
            <h1 className="mt-5 mb-0 text-[clamp(44px,7vw,78px)] leading-[0.98] font-medium tracking-[-0.05em] text-balance">
              This page has flown the nest.
            </h1>
            <p className="mt-6 mb-0 max-w-[560px] text-lg leading-[1.65] text-pretty text-ink-soft">
              We could not find the page you were looking for. It may have
              moved, or the link might be out of date.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button
                className="h-12 rounded-full bg-accent px-6 text-base text-ink shadow-card hover:bg-accent/80"
                render={<Link to="/" />}
              >
                Back to Budgy
              </Button>
              <Button
                className="h-12 rounded-full border-line-strong bg-white px-6 text-base text-ink shadow-card hover:bg-field"
                render={<Link to="/blog" />}
                variant="outline"
              >
                Read landlord advice
              </Button>
            </div>
          </section>

          <div
            aria-hidden="true"
            className="relative mx-auto flex aspect-square w-full max-w-[440px] items-center justify-center"
          >
            <div className="absolute inset-[8%] rounded-full bg-white shadow-card" />
            <p className="relative m-0 text-[clamp(112px,22vw,190px)] leading-none font-semibold tracking-[-0.09em] text-chip select-none lg:text-[170px]">
              404
            </p>
            <div className="absolute top-1/2 left-1/2 flex size-28 -translate-x-1/2 -translate-y-1/2 rotate-3 items-center justify-center rounded-full bg-accent-tint shadow-card sm:size-32">
              <BudgyMark className="size-20 sm:size-24" />
            </div>
            <div className="absolute top-[12%] right-[12%] size-3 rounded-full bg-accent" />
            <div className="absolute right-[5%] bottom-[24%] size-5 rounded-full bg-chip" />
          </div>
        </main>

        <footer className="pb-8 text-sm text-ink-muted">
          © {new Date().getFullYear()} Budgy
        </footer>
      </div>
    </div>
  );
};

export default NotFoundPage;
